import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import masterService from '../../../services/masterService';
import { useUser } from '../../../context/UserContext';

const { Title, Text } = Typography;
const { Search } = Input;

const PaymentModeList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [paymentModes, setPaymentModes] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} payment modes`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: ''
  });

  const getPaymentModes = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.search,
        ...params
      };
      
      console.log('Fetching payment modes with params:', requestParams);
      const response = await masterService.getPaymentModes(requestParams);
      console.log('Payment Modes API Response:', response);
      
      if (response.data && response.data.status) {
        const paymentModesData = response.data.data?.data || [];
        const paginationData = response.data.data?.pagination || {};
        
        console.log('Payment modes data:', paymentModesData);
        console.log('Pagination data:', paginationData);
        
        // Ensure paymentModesData is an array
        if (Array.isArray(paymentModesData)) {
          setPaymentModes(paymentModesData);
          setPagination(prev => ({
            ...prev,
            total: paginationData.total || paymentModesData.length,
            current: paginationData.currentPage || prev.current
          }));
        } else {
          console.error('Payment modes data is not an array:', paymentModesData);
          setPaymentModes([]);
        }
      } else {
        message.error('Failed to fetch payment modes');
        setPaymentModes([]);
      }
    } catch (error) {
      console.error('Error fetching payment modes:', error);
      message.error('Failed to fetch payment modes');
      setPaymentModes([]);
    } finally {
      setLoading(false);
    }
  }, [filters.search, pagination.pageSize, pagination.current]);

  // Load payment modes data
  useEffect(() => {
    getPaymentModes();
  }, [getPaymentModes]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getPaymentModes({
      searchText: value,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    getPaymentModes({
      currentPage: current,
      perPage: pageSize,
      searchText: filters.search
    });
  };

  // Handle add payment mode
  const handleAddPaymentMode = () => {
    navigate('/master/addpaymentmode');
  };

  // Handle edit payment mode
  const handleEditPaymentMode = (pmId) => {
    navigate(`/master/editpaymentmode/${pmId}`);
  };

  // Table columns
  const columns = [
    {
      title: 'Payment Mode',
      dataIndex: 'pm_payment_mode',
      key: 'pm_payment_mode',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.pm_payment_mode.localeCompare(b.pm_payment_mode),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'pm_is_active',
      key: 'pm_is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditPaymentMode(record.pm_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage Payment Modes</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Payment Mode List"
      desc="Manage payment modes and their details"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              Payment Modes ({pagination.total})
            </Title>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddPaymentMode}
              size="large"
            >
              Add Payment Mode
            </Button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Search
              placeholder="Search payment modes..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={paymentModes}
          rowKey="pm_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          size="middle"
          locale={{ 
            emptyText: 'No payment modes found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default PaymentModeList;